import React from 'react';
import MessageListItem from './MessageListItem';
import { 
  Box, 
  Typography, 
  List,
  Skeleton,
  Fade,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Drafts as DraftsIcon,
  Send as SendIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const MessageList = ({ folder, threads = [], search, selectedThread, onSelectThread, loading }) => {
  
  const getEmptyState = () => {
    const emptyStates = {
      inbox: { 
        icon: InboxIcon, 
        title: 'Inbox is empty',
        subtitle: 'Messages you receive will appear here',
      },
      outbox: { 
        icon: DraftsIcon, 
        title: 'No drafts',
        subtitle: 'Messages you save as drafts will appear here',
      },
      sent: { 
        icon: SendIcon, 
        title: 'No sent messages',
        subtitle: 'Messages you send will appear here',
      },
      archived: { 
        icon: ArchiveIcon, 
        title: 'No archived messages',
        subtitle: 'Archived messages will appear here',
      },
      trash: { 
        icon: DeleteIcon, 
        title: 'Trash is empty',
        subtitle: 'Deleted messages will appear here',
      },
    };
    return emptyStates[folder] || emptyStates.inbox;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
      
      if (isToday) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (isYesterday) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 1,
      }}>
        {[...Array(5)].map((_, i) => (
          <Box key={i} sx={{ p: 1.5, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={44} height={44} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="80%" height={18} />
                <Skeleton variant="text" width="40%" height={16} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (threads.length === 0) {
    const emptyState = getEmptyState();
    const EmptyIcon = emptyState.icon;
    
    return (
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        textAlign: 'center',
      }}>
        <EmptyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
          {emptyState.title}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {emptyState.subtitle}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flex: 1, 
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        width: 6,
      },
      '&::-webkit-scrollbar-track': {
        bgcolor: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        bgcolor: 'rgba(0,0,0,0.15)',
        borderRadius: 3,
        '&:hover': {
          bgcolor: 'rgba(0,0,0,0.25)',
        },
      },
    }}>
      <List disablePadding>
        {threads.map((thread, idx) => (
          <Fade in key={thread.id} timeout={{ enter: 200 + idx * 50 }}>
            <Box>
              <MessageListItem
                message={{
                  from: thread.isDraft ? 'Draft' : (thread.lastMessageSender || 'Unknown'),
                  avatar: '',
                  subject: thread.subject,
                  preview: thread.isDraft ? `Draft: ${thread.lastMessage || 'No content'}` : thread.lastMessage,
                  timestamp: formatTimestamp(thread.lastMessageAt),
                  unread: false,
                  priority: thread.priority || 'Normal',
                  isDraft: thread.isDraft,
                }}
                selected={selectedThread && selectedThread.id === thread.id}
                onClick={() => onSelectThread(thread)}
              />
            </Box>
          </Fade>
        ))}
      </List>
    </Box>
  );
};

export default MessageList;
